/**
 * The sales document, in the API's field names.
 *
 * The API speaks snake_case — `{header, details, discounts}`. The sales
 * document form keeps its own state in PascalCase, because those names are
 * threaded through its template, its validation rules and a hundred lines of
 * consignment arithmetic; renaming all of that would be a large change to the
 * screen that raises documents, for no benefit. So the translation happens at
 * the boundary instead: read in the component, write here.
 *
 * Every key is listed explicitly rather than transformed by a camel-to-snake
 * rule. A rule would quietly turn `UOM` into `u_o_m` and `DocNo` into
 * `doc_no` — one right, one wrong — and a wrong key on a write is the failure
 * that does not announce itself: the API accepts the document and stores that
 * field blank.
 */

const HEADER_KEYS = {
  DebtorCode: 'debtor_code',
  DocDate: 'doc_date',
  Description: 'description',
  CurrencyCode: 'currency_code',
  Agent: 'agent',
  InclusiveTax: 'inclusive_tax',
  IssuedBy: 'issued_by',
  ReceivedBy: 'received_by',
  IssuedBySignature: 'issued_by_signature',
  ReceivedBySignature: 'received_by_signature',
  // A discount over the whole document. The API folds it into the lines before
  // posting; the mode and value are the user's statement of it.
  DocDiscountMode: 'doc_discount_mode',
  DocDiscountValue: 'doc_discount_value',
}

const LINE_KEYS = {
  ItemCode: 'item_code',
  Description: 'description',
  Qty: 'qty',
  UOM: 'uom',
  UnitPrice: 'unit_price',
  Discount: 'discount',
  // The line's own discount as typed — a percentage or an amount. Separate from
  // `Discount` above, which is what the ledger ends up storing.
  DiscountMode: 'discount_mode',
  DiscountValue: 'discount_value',
  SubTotal: 'sub_total',
  TaxCode: 'tax_code',
  OpeningQty: 'opening_qty',
  SoldQty: 'sold_qty',
  AddReturnQty: 'add_return_qty',
  BalanceQty: 'balance_qty',
  StarterQty: 'starter_qty',
  ExchangeQty: 'exchange_qty',
}

// Alternative offers riding on a quotation line (One Sales-only; the ledger
// keeps the main line). Same key style as the line itself.
const OPTION_KEYS = {
  UOM: 'uom',
  Qty: 'qty',
  UnitPrice: 'unit_price',
  Discount: 'discount',
  TaxCode: 'tax_code',
  Description: 'description',
}

const DISCOUNT_KEYS = {
  Type: 'type',
  ItemCode: 'item_code',
  Qty: 'qty',
  Mode: 'mode',
  Value: 'value',
  Amount: 'amount',
}

// Only keys actually present are emitted, so a partial payload stays partial
// rather than sending a wall of nulls the API would write over the record.
function translate(source, keys) {
  const out = {}
  Object.entries(keys).forEach(([ours, theirs]) => {
    if (source && source[ours] !== undefined) out[theirs] = source[ours]
  })
  return out
}

export function toApiHeader(header) {
  return translate(header, HEADER_KEYS)
}

export function toApiLine(line) {
  const out = translate(line, LINE_KEYS)
  // `options` is emitted only when the line actually carries alternatives, so
  // every other document type keeps posting exactly what it posted before.
  if (Array.isArray(line?.options) && line.options.length) {
    out.options = line.options.map((o) => translate(o, OPTION_KEYS))
  }
  return out
}

export function toApiDiscount(discount) {
  return translate(discount, DISCOUNT_KEYS)
}
